import { Trans } from "@lingui/react/macro";
import React, { useCallback, useRef } from "react";
import {
  ButtonsContainer,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import * as styles from "./GlobalSearch.module.scss";
import { Index, useCurrentRefinements } from "react-instantsearch";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import PageResultsList from "./PageResultsList/PageResultsList";
import SeeAllCruisesButton from "./SeeAllCruisesButton";
import StickySearchContainer from "./StickySearchContainer/StickySearchContainer";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { CruiseStats } from "components/Algolia/CruiseStats";
import { PageStats } from "components/Algolia/PageStats";
import CruisesSliderHits from "components/Algolia/CruisesSliderHits";
import { ConfiguredInstantSearch } from "components/Algolia/ConfiguredInstantSearch";
import { buildGlobalSearchAlgoliaRouting } from "./routing";
import {
  FycPromosContentFragment,
  FycShipsContentFragment
} from "types/graphql-types";
import { scrollToSection } from "src/utils/inPageNavigation";
import { cruiseSearchIndexName, globalSearchIndexName } from "src/algolia";
import ConfiguredConfigure from "components/Algolia/ConfiguredConfigure";

interface GlobalSearchProps {
  fycPagePath?: string;
  cruisePromosContent: FycPromosContentFragment[];
  shipsContent: FycShipsContentFragment[];
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  fycPagePath,
  cruisePromosContent,
  shipsContent
}) => {
  const language = useLanguage();
  const { algoliaArea } = useGeolocation();

  const localizedCruiseSearchIndexName = algoliaArea
    ? cruiseSearchIndexName(algoliaArea)
    : undefined;

  if (!localizedCruiseSearchIndexName) return null;

  const localizedGlobalSearchIndexName = globalSearchIndexName(language);
  const algoliaRouting = buildGlobalSearchAlgoliaRouting(
    localizedCruiseSearchIndexName
  );

  return (
    <ConfiguredInstantSearch
      indexName={localizedCruiseSearchIndexName}
      routing={algoliaRouting}
    >
      <GlobalSearchBox
        localizedCruiseSearchIndexName={localizedCruiseSearchIndexName}
        localizedGlobalSearchIndexName={localizedGlobalSearchIndexName}
        fycPagePath={fycPagePath}
        cruisePromosContent={cruisePromosContent}
        shipsContent={shipsContent}
      />
    </ConfiguredInstantSearch>
  );
};

interface GlobalSearchBoxProps {
  localizedCruiseSearchIndexName: string;
  localizedGlobalSearchIndexName: string;
  fycPagePath?: string;
  cruisePromosContent: FycPromosContentFragment[];
  shipsContent: FycShipsContentFragment[];
}

const GlobalSearchBox: React.FC<GlobalSearchBoxProps> = ({
  localizedCruiseSearchIndexName,
  localizedGlobalSearchIndexName,
  fycPagePath,
  cruisePromosContent,
  shipsContent
}) => {
  const { items } = useCurrentRefinements({
    includedAttributes: ["query"]
  });

  const hasSearchQuery = items.length > 0;

  const cruiseQueryBuilder = new FYCQueryBuilder();
  cruiseQueryBuilder.onlyFutureCruises();
  if (process.env.GATSBY_SHOW_ALL_FUTURE_CRUISES != "true")
    cruiseQueryBuilder.onlyVisibleCruises();

  const resultsRef = useRef<HTMLDivElement>(null);

  const resetScrollOnPageClick = useCallback(() => {
    if (resultsRef.current) scrollToSection(resultsRef.current);
  }, []);

  return (
    <div>
      <StickySearchContainer className={styles.stickySearchContainer} />
      <div>
        {!hasSearchQuery && (
          <div className={styles.emptySearchContainer}>
            <Trans id="empty-search-query-text">
              You can utilize this page by simply typing your search request in
              the search field.
            </Trans>
          </div>
        )}
      </div>

      <div style={{ display: hasSearchQuery ? "block" : "none" }}>
        <SectionContainer
          backgroundColor="neutral05"
          className={styles.defaultContainer}
        >
          <React.Fragment>
            <div
              className={styles.searchSection}
              data-content-name="cruise_results"
            >
              <h3 className={styles.searchSectionTitle}>
                <Trans id="search-section-cruises-title">Cruises</Trans>
              </h3>

              <Index indexName={localizedCruiseSearchIndexName}>
                <div className={styles.resultsStats}>
                  <CruiseStats />
                </div>
                <ConfiguredConfigure
                  hitsPerPage={3}
                  filters={cruiseQueryBuilder.generate()}
                />
                <CruisesSliderHits
                  variantColor="white"
                  withMask={false}
                  variant="normal"
                  promosContent={cruisePromosContent}
                  shipsContent={shipsContent}
                />
                <br />
                <ButtonsContainer
                  align="center"
                  spacingTop="regular"
                  mobileFullSize
                >
                  <SeeAllCruisesButton fycPagePath={fycPagePath} />
                </ButtonsContainer>
              </Index>
            </div>
            <div
              ref={resultsRef}
              className={styles.searchSection}
              data-content-name="page_results"
            >
              <h3 className={styles.searchSectionTitle}>
                <Trans id="search-section-page-title">Pages</Trans>
              </h3>
              <Index indexName={localizedGlobalSearchIndexName}>
                <ConfiguredConfigure hitsPerPage={10} />
                <div className={styles.resultsStats}>
                  <PageStats />
                </div>
                <PageResultsList onPageChange={resetScrollOnPageClick} />
              </Index>
            </div>
          </React.Fragment>
        </SectionContainer>
      </div>
    </div>
  );
};

export default GlobalSearch;
